/*
 * whero-flavor-modal.js — opens the flavor detail <dialog> when a flavor
 * card is clicked, and populates the modal's sizes + allergens from the
 * JSON data attributes the card snippet renders.
 *
 * Why a shared asset: the default-mode card snippet
 * (snippets/whero-flavor-grid-card.liquid) ships a clickable
 * <article role="button" data-modal-target> plus a matching <dialog> on
 * EVERY surface — homepage grid, current-flavors, dirty sodas, location
 * pages, the new-customer landing, the vote archive, article call-outs.
 * Previously only whero-flavor-grid and whero-current-flavors wired the
 * click, so cards on the other surfaces looked clickable but did nothing.
 * This script wires them all from one place.
 *
 * Document-wide + idempotent: every modal id is unique (the section
 * prefixes card_id with section.id), so binding across the whole document
 * is safe even with multiple grids on one page. Per-element dataset guards
 * mean this is a no-op if it runs more than once (multiple sections each
 * include the script; re-render after a section reload, etc.).
 *
 * Interactive-element guard: a card may contain its own controls — the
 * inline Klaviyo notify-me form on sold-out cards. Clicks/keys originating
 * inside .whero-notify (or anything marked [data-no-modal]) must NOT open
 * the modal, so those inputs/buttons keep working.
 */
(function () {
  // ---- background scroll lock ----------------------------------------
  // Without this, scrolling over an open modal scrolls the page behind it
  // instead of the modal's own content. We hide overflow on <body> while
  // any modal is open and restore it once the last one closes. A counter
  // keeps it correct if more than one dialog is ever open at a time.
  var openCount = 0;
  var savedOverflow = '';
  var savedPaddingRight = '';

  function lockScroll() {
    if (openCount === 0) {
      savedOverflow = document.body.style.overflow;
      // Compensate for the disappearing scrollbar so the page behind the
      // backdrop doesn't shift sideways when we hide overflow.
      var scrollbar = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbar > 0) {
        savedPaddingRight = document.body.style.paddingRight;
        document.body.style.paddingRight = scrollbar + 'px';
      }
      document.body.style.overflow = 'hidden';
    }
    openCount++;
  }

  function unlockScroll() {
    if (openCount > 0) openCount--;
    if (openCount === 0) {
      document.body.style.overflow = savedOverflow;
      document.body.style.paddingRight = savedPaddingRight;
      savedOverflow = '';
      savedPaddingRight = '';
    }
  }

  function openModalFor(card) {
    var m = document.getElementById(card.dataset.modalTarget);
    if (m && typeof m.showModal === 'function') {
      // showModal() throws if the dialog is already open — guard it so a
      // stray double-bind or rapid clicks can't surface an error.
      if (!m.open) {
        m.showModal();
        lockScroll();
      }
    }
  }

  // True when the event came from a control that should handle its own
  // interaction (the notify-me form, or anything opted out via data-no-modal).
  function fromInteractive(e) {
    var t = e.target;
    return !!(t && t.closest && t.closest('.whero-notify, [data-no-modal]'));
  }

  function wireCards(scope) {
    scope.querySelectorAll('[data-modal-target]').forEach(function (card) {
      if (card.dataset.wheroModalWired === '1') return;
      card.dataset.wheroModalWired = '1';

      card.addEventListener('click', function (e) {
        if (fromInteractive(e)) return;
        openModalFor(card);
      });
      card.addEventListener('keydown', function (e) {
        if (fromInteractive(e)) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openModalFor(card);
        }
      });
    });
  }

  function wireDialogs(scope) {
    // Close button
    scope.querySelectorAll('.whero-flavor-card__modal-close').forEach(function (btn) {
      if (btn.dataset.wheroModalWired === '1') return;
      btn.dataset.wheroModalWired = '1';
      btn.addEventListener('click', function () {
        var d = btn.closest('dialog');
        if (d) d.close();
      });
    });

    // Backdrop click closes
    scope.querySelectorAll('.whero-flavor-card__modal').forEach(function (d) {
      if (d.dataset.wheroModalWired === '1') return;
      d.dataset.wheroModalWired = '1';
      d.addEventListener('click', function (e) {
        if (e.target === d) d.close();
      });
      // Restore background scroll on EVERY close path. The native `close`
      // event fires for the close button, the backdrop click (both via
      // d.close()), and the Escape key — so one listener covers them all.
      d.addEventListener('close', unlockScroll);
    });
  }

  function renderSizes(scope) {
    scope.querySelectorAll('.whero-flavor-card__modal-sizes ul[data-sizes]').forEach(function (ul) {
      if (ul.dataset.wheroModalRendered === '1') return;
      ul.dataset.wheroModalRendered = '1';

      var wrap = ul.closest('.whero-flavor-card__modal-sizes');
      var sizes = [];
      try { sizes = JSON.parse(ul.dataset.sizes || '[]'); } catch (_) {}
      if (!Array.isArray(sizes) || sizes.length === 0) {
        if (wrap) wrap.remove();
        return;
      }
      ul.innerHTML = sizes.map(function (s) {
        var name = (s && s.name) ? String(s.name) : '';
        var priceNum = s && typeof s.price === 'number' ? s.price : Number(s && s.price);
        var price = Number.isFinite(priceNum) ? '$' + priceNum.toFixed(2).replace(/\.00$/, '') : '';
        return '<li>' + name + (price ? ' — ' + price : '') + '</li>';
      }).join('');
      if (wrap) wrap.hidden = false;
    });
  }

  function renderAllergens(scope) {
    scope.querySelectorAll('.whero-flavor-card__modal-allergen-list').forEach(function (el) {
      if (el.dataset.wheroModalRendered === '1') return;
      el.dataset.wheroModalRendered = '1';

      try {
        var a = JSON.parse(el.dataset.allergens || '[]');
        if (Array.isArray(a) && a.length) {
          el.textContent = a.map(function (x) {
            return x.charAt(0).toUpperCase() + x.slice(1).toLowerCase();
          }).join(', ');
        } else {
          var w = el.closest('.whero-flavor-card__modal-allergens');
          if (w) w.remove();
        }
      } catch (_) {
        var w2 = el.closest('.whero-flavor-card__modal-allergens');
        if (w2) w2.remove();
      }
    });
  }

  function init(scope) {
    var root = scope || document;
    wireCards(root);
    wireDialogs(root);
    renderSizes(root);
    renderAllergens(root);
  }

  // Expose so a section's own JS (e.g. current-flavors after it re-renders
  // cards for a shop switch) can re-run wiring on freshly injected nodes.
  window.wheroFlavorModalInit = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Shopify theme-editor: re-wire when a section is re-rendered in the
  // customizer so previews stay clickable without a full reload.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
